/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {UserIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";

interface BookingPackagesViewSkeletonProps {
    /** Extra class(es) for the root element, e.g. the view's marker class. */
    className?: string;
}

/**
 * Loading placeholder shared by the packages / credits booking views while the
 * available package items are being fetched.
 */
const BookingPackagesViewSkeleton = ({className}: BookingPackagesViewSkeletonProps) => {
    const {t} = useTranslation();

    return (
        <div className={clsx(className, "w-full")}>
            <div className={"border rounded-md lg:px-1 lg:py-1 xl:px-4 xl:py-3 bg-white shadow"}>
                <table className="w-full lg:border-separate lg:border lg:border-spacing-y-2 lg:border-spacing-x-2 lg:border-white">
                    <colgroup className="hidden lg:table-column-group">
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="min-w-[110px] w-auto" />
                    </colgroup>
                    <thead className="hidden lg:table-header-group">
                        <tr>
                            <th className="text-left font-bold text-lg text-black"></th>
                            <th className="font-bold text-lg text-black">{t("Options")}</th>
                            <th className="font-bold text-lg text-black">{t("Select visit duration")}</th>
                            <th className="font-bold text-lg text-black">{t("Price")}</th>
                        </tr>
                    </thead>
                    <tbody className="flex flex-col lg:table-row-group">
                        <tr className="flex flex-col lg:table-row last-of-type:border-b-0 border-b lg:border-b-0 px-2 pt-3 pb-2 sm:w-auto sm:px-4 sm:py-4 lg:px-0 lg:py-0">
                            <td className="align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell border-b border-dashed sm:border-b-0">
                                <div className="flex flex-row items-center justify-start mb-2 sm:mb-0">
                                    <UserIcon className={clsx("h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2")} />
                                    <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                    <span className="font-bold text-sm xl:text-base">1. {t("Person")}</span>
                                </div>
                            </td>
                            <td className="align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                    {t("Options")}
                                    <span>:</span>
                                </h4>
                                <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                            </td>
                            <td className="align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                    Ticket nach Stunden<span>:</span>
                                </h4>
                                <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                            </td>
                            <td className="align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                    {t("Price")}
                                    <span>:</span>
                                </h4>
                                <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BookingPackagesViewSkeleton;
